import axios, { AxiosError } from 'axios';
import { env } from '@/config/env';
import { API_TIMEOUT } from '@/config/constants';
import { supabase } from '@/lib/supabase';

/**
 * Axios client configurado para el backend FastAPI
 *
 * Optimizaciones:
 * - Session token cached (no re-fetch per request)
 * - Retry with exponential backoff on 5xx/network errors
 * - Timeout configurable
 */
export const apiClient = axios.create({
  baseURL: env.apiUrl + '/api',
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// =============================================
// Session token cache - avoids async getSession() on every request
// =============================================
let _cachedToken: string | null = null;
let _tokenExpiresAt = 0;

/** Config de axios con las banderas internas de reintento */
type RetriableConfig = { _retryCount?: number; _authRetried?: boolean };

function getCachedToken(): string | null {
  if (_cachedToken && Date.now() < _tokenExpiresAt) {
    return _cachedToken;
  }
  return null;
}

// Listen for auth changes to update cached token
supabase.auth.onAuthStateChange((_event, session) => {
  if (session?.access_token) {
    _cachedToken = session.access_token;
    // Cache until 60s before expiry (or 50 min if no expires_at)
    _tokenExpiresAt = session.expires_at
      ? (session.expires_at * 1000) - 60_000
      : Date.now() + 50 * 60 * 1000;
  } else {
    _cachedToken = null;
    _tokenExpiresAt = 0;
  }
});

/**
 * Request interceptor - uses cached token, falls back to getSession()
 */
apiClient.interceptors.request.use(
  async (config) => {
    // Fast path: use cached token
    let token = getCachedToken();

    // Slow path: fetch session only if cache miss
    if (!token) {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        token = session.access_token;
        _cachedToken = token;
        _tokenExpiresAt = session.expires_at
          ? (session.expires_at * 1000) - 60_000
          : Date.now() + 50 * 60 * 1000;
      }
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Response interceptor - retry on 5xx with backoff, handle auth errors
 */
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ detail?: string; message?: string }>) => {
    const config = error.config;
    const status = error.response?.status;

    // Retry on 5xx or network error (max 2 retries)
    const retryCount = (config as any)?._retryCount || 0;
    if (config && retryCount < 2 && (status === 502 || status === 503 || status === 504 || !error.response)) {
      (config as any)._retryCount = retryCount + 1;
      const delay = Math.min(1000 * Math.pow(2, retryCount), 4000);
      await new Promise(r => setTimeout(r, delay));
      return apiClient(config);
    }

    // Sesión inválida o ausente. El backend responde 422 cuando falta el header
    // Authorization (Header obligatorio), así que ambos casos son "no autenticado".
    const isAuthFailure =
      status === 401 || (status === 422 && !config?.headers?.Authorization);

    if (isAuthFailure && config && !(config as RetriableConfig)._authRetried) {
      _cachedToken = null;
      _tokenExpiresAt = 0;

      // Un solo intento de refresco: si el token solo estaba vencido, la
      // petición continúa sin que el usuario note nada.
      try {
        const { data, error: refreshError } = await supabase.auth.refreshSession();
        if (!refreshError && data.session?.access_token) {
          _cachedToken = data.session.access_token;
          _tokenExpiresAt = data.session.expires_at
            ? (data.session.expires_at * 1000) - 60_000
            : Date.now() + 50 * 60 * 1000;

          (config as RetriableConfig)._authRetried = true;
          config.headers.Authorization = `Bearer ${_cachedToken}`;
          return apiClient(config);
        }
      } catch {
        // cae al cierre de sesión
      }

      // No se pudo refrescar: cerrar sesión y mandar al login en vez de dejar
      // la app en estado "zombie" (autenticada en localStorage, todo fallando).
      await supabase.auth.signOut().catch(() => undefined);
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
        window.location.assign('/login?expired=1');
      }
    }

    // Handle 429 - Usage limit exceeded
    if (status === 429) {
      const detail = error.response?.data?.detail || 'Limite mensual alcanzado';
      // Dispatch custom event so UI can show upgrade modal
      window.dispatchEvent(new CustomEvent('billing:limit-reached', { detail }));
    }

    // Minimal logging
    if (process.env.NODE_ENV === 'development') {
      console.error('[API]', error.config?.method?.toUpperCase(), error.config?.url, status || 'NETWORK_ERROR');
    }

    return Promise.reject(error);
  }
);

/**
 * Helper para convertir AxiosError a mensaje legible
 */
export function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.detail || error.message;
  }
  return error instanceof Error ? error.message : 'Error desconocido';
}
